// Geometry for cropping an image to a square. "view" is the on-screen square's side in pixels,
// "scale" is screen pixels per image pixel, and "offset" is where the image's top-left corner
// sits relative to the square's top-left corner (zero or negative on both axes).

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

// The smallest scale at which the image still covers the whole square.
export const coverScale = (image: Size, view: number): number => view / Math.min(image.width, image.height);

export const clampOffset = (offset: Point, image: Size, scale: number, view: number): Point => ({
  x: Math.min(0, Math.max(view - image.width * scale, offset.x)),
  y: Math.min(0, Math.max(view - image.height * scale, offset.y)),
});

export const centeredOffset = (image: Size, scale: number, view: number): Point => ({
  x: (view - image.width * scale) / 2,
  y: (view - image.height * scale) / 2,
});

// Changes the scale while keeping the image point at the middle of the square in place.
export const zoomAtCenter = (offset: Point, scale: number, nextScale: number, image: Size, view: number): Point => {
  const centerX = (view / 2 - offset.x) / scale;
  const centerY = (view / 2 - offset.y) / scale;
  return clampOffset(
    { x: view / 2 - centerX * nextScale, y: view / 2 - centerY * nextScale },
    image,
    nextScale,
    view
  );
};

// The part of the original image, in image pixels, that the square currently shows.
export const sourceSquare = (offset: Point, scale: number, view: number): { x: number; y: number; size: number } => ({
  x: (0 - offset.x) / scale,
  y: (0 - offset.y) / scale,
  size: view / scale,
});

export const MAX_OUTPUT_SIZE = 1024;

export const outputSize = (sourceSize: number): number => Math.min(MAX_OUTPUT_SIZE, Math.round(sourceSize));
